(ns app.connect-link.modal
  "Modal for creating and managing connect-links."
  (:require [storrito.ui.core :as ui]
            [parts.replicant.query :as query]))

(def delete-command
  {:command/kind :command/connect-link-delete})

(def connect-link-query
  {:query/kind :query/connect-link-list})

(defn connect-link-url
  [uuid]
  (str js/window.location.protocol "//" js/window.location.host
       "/ui/connect3?connect-link=" uuid))

(def ^:private copy-btn-class
  ["inline-flex" "items-center" "px-2" "py-1" "text-xs"
   "font-medium" "text-gray-600" "bg-gray-100" "rounded"
   "hover:bg-gray-200" "cursor-pointer"])

(defn format-date
  [date]
  (when date
    (.toLocaleDateString (js/Date. date)
                         js/undefined
                         #js {:year "numeric"
                              :month "short"
                              :day "numeric"})))

(defn link-entry
  [state entry]
  (let [uuid (str (:connect-link/uuid entry))
        url (connect-link-url uuid)
        copy-key (keyword "connect-link.copied" uuid)]
    [:div.flex.items-center.justify-between.py-3.border-b.border-gray-100
     {:key uuid}
     [:div.flex-1.min-w-0.mr-3
      [:p.text-sm.font-medium.text-gray-900.truncate
       (or (:connect-link/description entry) "No description")]
      [:p.text-xs.text-gray-500
       (format-date (:connect-link/created-at entry))
       (when-let [created-by (:connect-link/created-by entry)]
         (str " by " created-by))]]
     [:div.flex.items-center.gap-2
      [:button
       {:class copy-btn-class
        :on {:click [[:clipboard/copy url copy-key]]}}
       (if (get state copy-key) "Copied!" "Copy")]
      [:button
       {:class ["inline-flex" "items-center" "px-2" "py-1" "text-xs"
                "font-medium" "text-red-600" "bg-red-50" "rounded"
                "hover:bg-red-100" "cursor-pointer"]
        :title "Delete"
        :on {:click [[:data/command
                      (assoc delete-command
                             :command/data {:connect-link/uuid uuid})
                      {:on-success [[:data/query connect-link-query]]}]]}}
       [:i.fas.fa-trash-alt]]]]))

(defn link-list
  [state]
  (let [result (get-in (query/get-result state connect-link-query)
                       [:connect-link])
        entries (:connect-link/entries result)]
    (when (seq entries)
      [:div.mt-6
       [:h4 {:class "text-sm font-medium text-gray-700 mb-2"}
        "Existing connect-links"]
       [:div {:style {:max-height "240px"
                      :overflow-y "auto"}
              :class "divide-y divide-gray-100"}
        (map (partial link-entry state) entries)]])))

(def max-connect-links 100)

(defn render
  [state]
  (let [entries (:connect-link/entries
                  (get-in (query/get-result state connect-link-query)
                          [:connect-link]))
        limit-reached? (>= (count entries) max-connect-links)
        created-uuid (:connect-link/created-uuid state)]
    (if created-uuid
      (let [url (connect-link-url created-uuid)]
        [:div.py-4
         [:div.text-center
          [:h3 {:class "text-base font-semibold leading-6 text-gray-900"}
           "Connect-link created"]
          [:p {:class "mt-2 text-sm text-gray-500"}
           "Share this link with your client. They can connect their Instagram account without needing a Storrito login."]]
         [:div.mt-4
          [:div.flex.items-center.justify-between
           [:label {:class "block text-sm font-medium leading-6 text-gray-900"}
            "Connect-link URL"]
           [:button
            {:class copy-btn-class
             :on {:click [[:clipboard/copy url :connect-link.copied/created]]}}
            (if (:connect-link.copied/created state) "Copied!" "Copy")]]
          [:div.mt-1
           [:input
            {:type "text"
             :readonly true
             :class "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 bg-gray-50 sm:text-sm sm:leading-6"
             :value url}]]]
         (link-list state)
         [:div.mt-6
          [:button
           {:class (concat ui/button-primary ["w-full"])
            :on {:click [[:ui/modal-close]]}}
           "Close"]]])
      (let [uuid (or (:connect-link/pending-uuid state)
                     (str (random-uuid)))]
        [:div.py-4
         [:div.text-center
          [:h3 {:class "text-base font-semibold leading-6 text-gray-900"}
           "Share a connect-link"]
          [:p {:class "mt-2 text-sm text-gray-500"}
           "Generate a secret link that allows an external person to connect their Instagram account to your Storrito organization — without needing their own login."]]
         (when limit-reached?
           [:div.mt-4
            [:div {:class "rounded-md bg-yellow-50 p-4"}
             [:div {:class "flex"}
              [:div {:class "flex-shrink-0"}
               [:i.fas.fa-lock.text-yellow-400]]
              [:div {:class "ml-3"}
               [:p {:class "text-sm text-yellow-700"}
                (str "Maximum of " max-connect-links " connect-links reached. Please delete unused links.")]]]]])
         [:div.mt-4
          [:label {:class "block text-sm font-medium leading-6 text-gray-900"}
           "Description (optional)"]
          [:div.mt-1
           [:input
            {:type "text"
             :placeholder "e.g. Client name or Instagram handle"
             :class "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
             :on {:input [[:store/assoc-in
                           [:connect-link/description]
                           :event/target.value]]}}]]]
         [:div.mt-6
          [:button
           {:class (concat ui/button-primary ["w-full"])
            :disabled limit-reached?
            :on {:click [[:data/command
                          {:command/kind :command/connect-link-create
                           :command/data
                           {:description (:connect-link/description state)
                            :connect-link/uuid uuid}}
                          {:on-success
                           [[:store/assoc-in
                             [:connect-link/created-uuid]
                             uuid]
                            [:data/query connect-link-query]]}]]}}
           "Generate connect-link"]]
         (link-list state)]))))

(def modal
  {:modal-id :modal/connect-link-create
   :on-load (fn [_]
              [[:store/assoc-in
                [:connect-link/created-uuid]
                nil]
               [:store/assoc-in
                [:connect-link/pending-uuid]
                (str (random-uuid))]
               [:data/query connect-link-query]])
   :render #'render})
